import { useEffect, useState } from 'react';

import { Environment } from '../constants/environments';
import { HttpVerb } from '../constants/httpVerbs';
import { ResaleListingGroup } from './contexts/resale-listing.context';
import { useApiService } from './useApiService';

// Upper bound for the resale check. Without it, a request that never responds would leave the
// tickets page stuck on the placeholder; on timeout, the request is aborted and we proceed without resale.
export const RESALE_AVAILABILITY_TIMEOUT_MS = 8000;

interface UseResaleAvailabilityCheckResult {
  hasResale: boolean;
  loading: boolean;
}

interface ResaleCheckResult {
  eventId: number;
  hasResale: boolean;
}

export function useResaleAvailabilityCheck(
  eventId: number | undefined
): UseResaleAvailabilityCheckResult {
  // Result stored together with the checked event: loading is derived from it, so the first
  // render with a new eventId is already loading, without waiting for the effect (which runs after
  // paint) and without inheriting another event's hasResale.
  const [result, setResult] = useState<ResaleCheckResult | null>(null);
  const { request } = useApiService();

  useEffect(() => {
    if (!eventId) return;

    let settled = false;
    const controller = new AbortController();
    const finish = (hasResale: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      setResult({ eventId, hasResale });
    };
    const timeoutId = setTimeout(() => {
      finish(false);
      controller.abort();
    }, RESALE_AVAILABILITY_TIMEOUT_MS);

    const check = async () => {
      try {
        const resourcePath = `/resale/event/${encodeURIComponent(String(eventId))}/grouped`
          + `?apikey=${Environment.API_KEY}`;
        const response = await request<null, { detail: { groups: ResaleListingGroup[] } }>(
          { requestOptions : { resourcePath, config : { signal : controller.signal } }, type : HttpVerb.GET },
          { baseUrl : Environment.API_GATEWAY_URL }
        );
        finish((response?.detail?.groups ?? []).length > 0);
      } catch {
        finish(false);
      }
    };

    check();
    return () => {
      settled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [eventId]);

  const checked = Boolean(eventId) && result?.eventId === eventId;
  return {
    hasResale : checked && Boolean(result?.hasResale),
    loading : Boolean(eventId) && !checked,
  };
}
